/**
 * Admin Category Management
 * Full CRUD for product categories
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Link2, ArrowLeft, Plus, Trash2, Edit2, X, RefreshCw, Layers, 
  Package, GripVertical, Eye, EyeOff, Check, FolderTree, Image
} from 'lucide-react';

const AdminCategoryManagement = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '', description: '', image: '', parent_id: '', display_order: 0, is_active: true
  });
  const [reassignTo, setReassignTo] = useState('');

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/categories?include_inactive=true');
      setCategories(response.data?.categories || []);
    } catch (error) {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = async () => {
    if (!formData.name.trim()) {
      toast.error('Category name is required');
      return;
    }
    setActionLoading(true);
    try {
      await api.post('/admin/categories', formData);
      toast.success('Category created!');
      setShowAddModal(false);
      setFormData({ name: '', description: '', image: '', parent_id: '', display_order: 0, is_active: true });
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create category');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateCategory = async () => {
    if (!formData.name.trim()) {
      toast.error('Category name is required');
      return;
    }
    setActionLoading(true);
    try {
      await api.put(`/admin/categories/${selectedCategory.id}`, formData);
      toast.success('Category updated!');
      setShowEditModal(false);
      setSelectedCategory(null);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update category');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteCategory = async () => {
    setActionLoading(true);
    try {
      const params = reassignTo ? `?reassign_to=${reassignTo}` : '';
      await api.delete(`/admin/categories/${selectedCategory.id}${params}`);
      toast.success('Category deleted!');
      setShowDeleteModal(false);
      setSelectedCategory(null);
      setReassignTo('');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleActive = async (category) => {
    try {
      await api.put(`/admin/categories/${category.id}`, { is_active: !category.is_active });
      toast.success(`Category ${!category.is_active ? 'activated' : 'deactivated'}`);
      fetchCategories();
    } catch (error) {
      toast.error('Failed to update category');
    }
  };

  const openEditModal = (category) => {
    setSelectedCategory(category);
    setFormData({
      name: category.name,
      description: category.description || '',
      image: category.image || '',
      parent_id: category.parent_id || '',
      display_order: category.display_order || 0,
      is_active: category.is_active
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (category) => {
    setSelectedCategory(category);
    setReassignTo('');
    setShowDeleteModal(true);
  };

  const stats = {
    total: categories.length,
    active: categories.filter(c => c.is_active).length,
    withProducts: categories.filter(c => c.product_count > 0).length,
    totalProducts: categories.reduce((sum, c) => sum + (c.product_count || 0), 0)
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate('/admin')} variant="ghost" size="sm" data-testid="back-button">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-3">
              <Link2 className="h-7 w-7 text-primary" />
              <span className="text-xl font-bold">Liantiao</span>
              <span className="text-xs font-mono text-purple-400 bg-purple-500/10 px-2 py-1 rounded border border-purple-500/20">
                CATEGORIES
              </span>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => { setFormData({ name: '', description: '', image: '', parent_id: '', display_order: categories.length, is_active: true }); setShowAddModal(true); }} className="bg-primary" data-testid="add-category-btn">
              <Plus className="h-4 w-4 mr-2" />Add Category
            </Button>
            <Button onClick={fetchCategories} variant="outline" size="sm" disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card><CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center"><Layers className="h-5 w-5 text-purple-400" /></div>
            <div><p className="text-sm text-muted-foreground">Total Categories</p><p className="text-2xl font-bold">{stats.total}</p></div>
          </CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center"><Check className="h-5 w-5 text-green-400" /></div>
            <div><p className="text-sm text-muted-foreground">Active</p><p className="text-2xl font-bold text-green-400">{stats.active}</p></div>
          </CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center"><FolderTree className="h-5 w-5 text-blue-400" /></div>
            <div><p className="text-sm text-muted-foreground">With Products</p><p className="text-2xl font-bold text-blue-400">{stats.withProducts}</p></div>
          </CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center"><Package className="h-5 w-5 text-yellow-400" /></div>
            <div><p className="text-sm text-muted-foreground">Total Products</p><p className="text-2xl font-bold text-yellow-400">{stats.totalProducts}</p></div>
          </CardContent></Card>
        </div>

        {/* Categories List */}
        <div className="space-y-3">
          {loading ? (
            <div className="text-center py-12"><RefreshCw className="h-8 w-8 text-primary animate-spin mx-auto" /></div>
          ) : categories.length === 0 ? (
            <Card className="p-12 text-center">
              <Layers className="h-12 w-12 mx-auto text-muted-foreground mb-4 opacity-30" />
              <p className="text-muted-foreground">No categories yet</p>
              <p className="text-sm text-muted-foreground mt-1">Create your first product category</p>
            </Card>
          ) : (
            categories.map((category, index) => (
              <Card key={category.id} className={`hover:border-primary/30 transition-colors ${!category.is_active ? 'opacity-60' : ''}`} data-testid={`category-${category.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="text-muted-foreground cursor-move"><GripVertical className="h-5 w-5" /></div>
                    
                    {/* Category Image/Icon */}
                    <div className="w-14 h-14 rounded-lg bg-muted flex items-center justify-center overflow-hidden shrink-0">
                      {category.image ? (
                        <img src={category.image} alt={category.name} className="w-full h-full object-cover" />
                      ) : (
                        <Layers className="h-6 w-6 text-muted-foreground" />
                      )}
                    </div>
                    
                    {/* Category Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-foreground">{category.name}</h3>
                        {!category.is_active && <Badge variant="outline" className="text-xs">Inactive</Badge>}
                      </div>
                      {category.description && <p className="text-sm text-muted-foreground line-clamp-1">{category.description}</p>}
                      <p className="text-xs text-muted-foreground mt-1">Slug: {category.slug}</p>
                    </div>
                    
                    {/* Product Count */}
                    <div className="text-center px-4">
                      <p className="text-2xl font-bold text-primary">{category.product_count || 0}</p>
                      <p className="text-xs text-muted-foreground">products</p>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handleToggleActive(category)} title={category.is_active ? 'Deactivate' : 'Activate'}>
                        {category.is_active ? <Eye className="h-4 w-4 text-green-400" /> : <EyeOff className="h-4 w-4 text-muted-foreground" />}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openEditModal(category)}><Edit2 className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="sm" className="text-destructive hover:bg-destructive/10" onClick={() => openDeleteModal(category)}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </main>

      {/* Add Category Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold flex items-center gap-2"><Plus className="h-5 w-5 text-primary" />Add Category</h2>
              <Button onClick={() => setShowAddModal(false)} variant="ghost" size="sm"><X className="h-5 w-5" /></Button>
            </div>
            <div className="space-y-4">
              <div><Label>Category Name *</Label><Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="e.g., Electronics, Clothing" data-testid="category-name-input" /></div>
              <div><Label>Description</Label><textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="w-full bg-background border border-input rounded-md p-2 text-sm min-h-[80px]" placeholder="Brief description of this category..." /></div>
              <div><Label className="flex items-center gap-2"><Image className="h-4 w-4" />Category Image URL</Label><Input value={formData.image} onChange={(e) => setFormData({...formData, image: e.target.value})} placeholder="https://..." /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Parent Category (optional)</Label>
                  <select value={formData.parent_id} onChange={(e) => setFormData({...formData, parent_id: e.target.value})} className="w-full h-10 bg-background border border-input rounded-md px-3 text-sm">
                    <option value="">No parent (top-level)</option>
                    {categories.filter(c => c.is_active).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div><Label>Display Order</Label><Input type="number" value={formData.display_order} onChange={(e) => setFormData({...formData, display_order: parseInt(e.target.value) || 0})} /></div>
              </div>
              <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-lg">
                <input type="checkbox" id="active" checked={formData.is_active} onChange={(e) => setFormData({...formData, is_active: e.target.checked})} className="w-4 h-4 rounded" />
                <Label htmlFor="active" className="cursor-pointer">Active (visible to customers)</Label>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button onClick={() => setShowAddModal(false)} variant="outline" className="flex-1">Cancel</Button>
              <Button onClick={handleAddCategory} className="flex-1 bg-primary" disabled={actionLoading}>
                {actionLoading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}Create Category
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Category Modal */}
      {showEditModal && selectedCategory && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold flex items-center gap-2"><Edit2 className="h-5 w-5 text-primary" />Edit Category</h2>
              <Button onClick={() => setShowEditModal(false)} variant="ghost" size="sm"><X className="h-5 w-5" /></Button>
            </div>
            <div className="space-y-4">
              <div><Label>Category Name *</Label><Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} /></div>
              <div><Label>Description</Label><textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="w-full bg-background border border-input rounded-md p-2 text-sm min-h-[80px]" /></div>
              <div><Label className="flex items-center gap-2"><Image className="h-4 w-4" />Category Image URL</Label><Input value={formData.image} onChange={(e) => setFormData({...formData, image: e.target.value})} placeholder="https://..." /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Parent Category</Label>
                  <select value={formData.parent_id} onChange={(e) => setFormData({...formData, parent_id: e.target.value})} className="w-full h-10 bg-background border border-input rounded-md px-3 text-sm">
                    <option value="">No parent (top-level)</option>
                    {categories.filter(c => c.is_active && c.id !== selectedCategory.id).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div><Label>Display Order</Label><Input type="number" value={formData.display_order} onChange={(e) => setFormData({...formData, display_order: parseInt(e.target.value) || 0})} /></div>
              </div>
              <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-lg">
                <input type="checkbox" id="edit-active" checked={formData.is_active} onChange={(e) => setFormData({...formData, is_active: e.target.checked})} className="w-4 h-4 rounded" />
                <Label htmlFor="edit-active" className="cursor-pointer">Active (visible to customers)</Label>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button onClick={() => setShowEditModal(false)} variant="outline" className="flex-1">Cancel</Button>
              <Button onClick={handleUpdateCategory} className="flex-1 bg-primary" disabled={actionLoading}>
                {actionLoading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}Update Category
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Category Modal */}
      {showDeleteModal && selectedCategory && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-destructive flex items-center gap-2"><Trash2 className="h-5 w-5" />Delete Category</h2>
              <Button onClick={() => setShowDeleteModal(false)} variant="ghost" size="sm"><X className="h-5 w-5" /></Button>
            </div>
            <p className="text-muted-foreground mb-4">
              Are you sure you want to delete <strong className="text-foreground">{selectedCategory.name}</strong>?
            </p>
            
            {selectedCategory.product_count > 0 && (
              <div className="mb-4 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                <p className="text-sm text-yellow-400 mb-3">
                  This category has <strong>{selectedCategory.product_count} products</strong>. 
                  Select a category to move them to, or they will be left without a category.
                </p>
                <Label className="text-sm">Reassign products to:</Label>
                <select value={reassignTo} onChange={(e) => setReassignTo(e.target.value)} className="w-full h-10 mt-1 bg-background border border-input rounded-md px-3 text-sm">
                  <option value="">-- Leave without category --</option>
                  {categories.filter(c => c.id !== selectedCategory.id && c.is_active).map(c => (
                    <option key={c.id} value={c.id}>{c.name} ({c.product_count} products)</option>
                  ))}
                </select>
              </div>
            )}
            
            <div className="flex gap-3">
              <Button onClick={() => setShowDeleteModal(false)} variant="outline" className="flex-1">Cancel</Button>
              <Button onClick={handleDeleteCategory} variant="destructive" className="flex-1" disabled={actionLoading}>
                {actionLoading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCategoryManagement;
